import { Button as GddsButton } from '@geberit/gdds';
import { useSelector } from 'react-redux';

// styles
import styles from './summary-container.module.scss';

// types
import type { CheckoutState } from 'components/checkout/reducer.types';

// components
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { AddressBox } from 'components/checkout/components/address-box/address-box';
import { Button, BUTTON_BACKGROUND, BUTTON_COLORS, BUTTON_TYPES } from 'components/Button/Button';
import OrderDetailsBox from 'components/checkout/components/order-details-box/order-details-box';

// utils
import { mapSapAndCiamCodesToSalutations } from 'components/checkout/util/helper-functions';
import { webshopLandingpageUrlSelector } from 'utils/selectors/globalsSelectors';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { isEmpty } from 'utils/is-empty';
import OrderContents from 'components/checkout/containers/order-contents/order-contents';
import { useVoucher } from 'utils/hooks/useVoucher';
import { classNameBuilder } from 'utils/classNameBuilder';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import { useXy } from 'utils/hooks/use-xy';

type SummaryDataProps = {
  orderId: string;
  orderData: CheckoutState['orderData'];
};

export function SummaryData({ orderId, orderData }: Readonly<SummaryDataProps>) {
  const translate = useTranslationFunction();
  const webshopHomeUrl = useSelector(webshopLandingpageUrlSelector);
  const voucher = useVoucher(orderData.invoiceAddress, orderData.deliveryAddress);
  const isXy = useXy();
  const gridClasses = isXy ? 'small-12 medium-12 large-4' : 'small-12 medium-4 large-4';

  const salutationTranslationKeyInvoiceAddress =
    orderData && !isEmpty(orderData.invoiceAddress) && orderData.invoiceAddress.salutation
      ? mapSapAndCiamCodesToSalutations(orderData.invoiceAddress.salutation)
      : '';
  const salutationTranslationKeyDeliveryAddress =
    orderData && !isEmpty(orderData.deliveryAddress) && orderData.deliveryAddress.salutation
      ? mapSapAndCiamCodesToSalutations(orderData.deliveryAddress.salutation)
      : '';

  return (
    <>
      <div className="grid-x">
        <div className={`cell ${gridClasses}`}>
          <OrderDetailsBox
            orderData={orderData.orderDetails}
            orderId={orderId}
            title={translate('web20_checkout_summary_order_heading')}
          />
        </div>
        <div className={`cell ${gridClasses}`}>
          <AddressBox
            country={translate(`countries_${orderData.invoiceAddress.country.toLowerCase()}`)}
            salutation={translate(salutationTranslationKeyInvoiceAddress)}
            address={{
              ...orderData.invoiceAddress,
              sapCustomerNumber: orderData.sapCustomerNumber,
            }}
            title={translate('web20_checkout_address_invoice_form_heading')}
          />
        </div>
        <div className={`cell ${gridClasses}`}>
          <AddressBox
            country={translate(`countries_${orderData.deliveryAddress.country.toLowerCase()}`)}
            salutation={translate(salutationTranslationKeyDeliveryAddress)}
            address={orderData.deliveryAddress}
            title={translate('web20_checkout_address_delivery_form_heading')}
            isDelivery
          />
        </div>
      </div>
      {isXy ? (
        <Headline
          tag={Formats.h4}
          variant={Formats.subtitle}
          className={classNameBuilder(isXy && styles.basketHeading, styles.bottomMargin)}
          title={translate('web20_checkout_summary_basket_table_heading')}
        />
      ) : (
        <h4 className="h4">{translate('web20_checkout_summary_basket_table_heading')}</h4>
      )}
      <OrderContents orderData={orderData} voucher={voucher} isSummaryPage />
      <div
        className={classNameBuilder(
          'cell grid-x grid-margin-x',
          styles.footerButtonRow,
          styles.topMargin,
          styles.bottomMargin,
        )}
      >
        {isXy ? (
          <GddsButton
            stylingType="primary"
            symbol="arrow_right"
            className="cell"
            onClick={() => {
              window.location.href = webshopHomeUrl ?? '';
            }}
          >
            {translate('web20_checkout_summary_button_next')}
          </GddsButton>
        ) : (
          <Button
            type={BUTTON_TYPES.DEFAULT}
            className="cell"
            symbol="arrow-link-light-right"
            color={BUTTON_COLORS.WHITE}
            background={BUTTON_BACKGROUND.PRIMARY}
            onClick={() => {
              window.location.href = webshopHomeUrl ?? '';
            }}
          >
            {translate('web20_checkout_summary_button_next')}
          </Button>
        )}
      </div>
    </>
  );
}
